/**
 * ─── HRIS Shift Management ──────────────────────────────────
 * Work shift definitions: Morning, Afternoon, Night, Custom.
 * Configurable start/end times, break periods, grace periods.
 */

import { useState } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { Clock, Plus, Pencil, Trash2, Sun, Moon, Sunset, Settings } from 'lucide-react'

interface ShiftRecord {
  id: string
  shiftCode: string
  name: string
  type: 'regular' | 'night_shift' | 'flexible'
  startTime: string
  endTime: string
  breakStart: string
  breakEnd: string
  gracePeriodMinutes: number
  isActive: boolean
  color: string
}

const STORAGE_KEY = 'hris_shifts'
const DEFAULT_SHIFTS: ShiftRecord[] = [
  { id: 'shift-morning', shiftCode: 'MORN', name: 'Morning Shift', type: 'regular', startTime: '06:00', endTime: '14:00', breakStart: '10:00', breakEnd: '10:30', gracePeriodMinutes: 15, isActive: true, color: '#F59E0B' },
  { id: 'shift-afternoon', shiftCode: 'AFTN', name: 'Afternoon Shift', type: 'regular', startTime: '14:00', endTime: '22:00', breakStart: '18:00', breakEnd: '18:30', gracePeriodMinutes: 15, isActive: true, color: '#3B82F6' },
  { id: 'shift-night', shiftCode: 'NGHT', name: 'Night Shift', type: 'night_shift', startTime: '22:00', endTime: '06:00', breakStart: '02:00', breakEnd: '02:30', gracePeriodMinutes: 30, isActive: true, color: '#6366F1' },
]

function load(): ShiftRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : [...DEFAULT_SHIFTS]
  } catch { return [...DEFAULT_SHIFTS] }
}

function save(shifts: ShiftRecord[]) { localStorage.setItem(STORAGE_KEY, JSON.stringify(shifts)) }

const TYPE_ICONS: Record<string, any> = { regular: Sun, night_shift: Moon, flexible: Settings }
const TYPE_LABELS: Record<string, string> = { regular: 'Regular', night_shift: 'Night Shift', flexible: 'Flexible' }

export function HrisShiftPage() {
  const [shifts, setShifts] = useState<ShiftRecord[]>(load)
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState({
    shiftCode: '', name: '', type: 'regular' as string,
    startTime: '08:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00',
    gracePeriodMinutes: 15, isActive: true, color: '#3B82F6',
  })

  const persist = (s: ShiftRecord[]) => { setShifts(s); save(s) }

  function reset() {
    setForm({ shiftCode: '', name: '', type: 'regular', startTime: '08:00', endTime: '17:00',
      breakStart: '12:00', breakEnd: '13:00', gracePeriodMinutes: 15, isActive: true, color: '#3B82F6' })
    setEditId(null)
  }

  function handleSave() {
    if (!form.name || !form.shiftCode) return
    const entry: ShiftRecord = {
      id: editId || 'shift-' + Date.now(),
      ...form,
      type: form.type as ShiftRecord['type'],
    }
    const updated = editId
      ? shifts.map(s => s.id === editId ? entry : s)
      : [entry, ...shifts]
    persist(updated)
    setShowModal(false)
    reset()
  }

  function handleDelete(id: string) {
    if (!confirm('Delete this shift?')) return
    persist(shifts.filter(s => s.id !== id))
  }

  function startEdit(s: ShiftRecord) {
    setForm({
      shiftCode: s.shiftCode, name: s.name, type: s.type,
      startTime: s.startTime, endTime: s.endTime,
      breakStart: s.breakStart, breakEnd: s.breakEnd,
      gracePeriodMinutes: s.gracePeriodMinutes, isActive: s.isActive, color: s.color,
    })
    setEditId(s.id)
    setShowModal(true)
  }

  const hours = (s: ShiftRecord) => {
    const [sh, sm] = s.startTime.split(':').map(Number) as [number, number]
    const [eh, em] = s.endTime.split(':').map(Number) as [number, number]
    let h = eh - sh + (em - sm) / 60
    if (h < 0) h += 24 // night shift wraps
    return h
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Shift Management</h2>
          <p className="mt-1 text-sm text-gray-500">Configure work shifts with start/end times, breaks, and grace periods</p>
        </div>
        <Button onClick={() => { reset(); setShowModal(true) }} icon={<Plus className="h-4 w-4" />}>
          Add Shift
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shifts.map(s => {
          const Icon = TYPE_ICONS[s.type] ?? Clock
          return (
            <div key={s.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg p-2.5" style={{ backgroundColor: s.color + '20' }}>
                    <Icon className="h-5 w-5" style={{ color: s.color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{s.name}</h3>
                    <p className="text-xs text-gray-500">{s.shiftCode}</p>
                  </div>
                </div>
                <Badge color={s.isActive ? 'green' : 'gray'}>{s.isActive ? 'Active' : 'Inactive'}</Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type</span>
                  <span className="font-medium">{TYPE_LABELS[s.type] ?? s.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Hours</span>
                  <span className="font-medium">
                    {s.startTime} – {s.endTime} ({hours(s).toFixed(1)}h)
                  </span>
                </div>
                {s.breakStart && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Break</span>
                    <span className="font-medium">{s.breakStart} – {s.breakEnd}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Grace Period</span>
                  <span className="font-medium">{s.gracePeriodMinutes} min</span>
                </div>
              </div>
              <div className="mt-4 flex gap-2 border-t border-gray-100 pt-3">
                <button onClick={() => startEdit(s)} className="rounded p-1.5 text-blue-600 hover:bg-blue-50" title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(s.id)} className="rounded p-1.5 text-red-600 hover:bg-red-50" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <Modal open={showModal} onClose={() => { setShowModal(false); reset() }}
        title={editId ? 'Edit Shift' : 'Add Shift'} size="md"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setShowModal(false); reset() }}>Cancel</Button>
            <Button onClick={handleSave} icon={<Clock className="h-4 w-4" />}>{editId ? 'Update' : 'Create'} Shift</Button>
          </div>
        }>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Shift Code" value={form.shiftCode} onChange={e => setForm({ ...form, shiftCode: e.target.value })} placeholder="MORN" />
          <Input label="Shift Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Morning Shift" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="regular">Regular</option>
              <option value="night_shift">Night Shift</option>
              <option value="flexible">Flexible</option>
            </select>
          </div>
          <Input label="Color" type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
          <Input label="Start Time" type="time" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} />
          <Input label="End Time" type="time" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} />
          <Input label="Break Start" type="time" value={form.breakStart} onChange={e => setForm({ ...form, breakStart: e.target.value })} />
          <Input label="Break End" type="time" value={form.breakEnd} onChange={e => setForm({ ...form, breakEnd: e.target.value })} />
          <Input label="Grace Period (min)" type="number" value={String(form.gracePeriodMinutes)}
            onChange={e => setForm({ ...form, gracePeriodMinutes: Number(e.target.value) })} />
          <div className="flex items-center gap-2 pt-5">
            <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300" />
            <label className="text-sm text-gray-700">Active</label>
          </div>
        </div>
      </Modal>
    </div>
  )
}
